import React from "react";
import { SwipeableDrawer, Popover, Box, useMediaQuery, useTheme } from "@mui/material";
import { TOKENS, FONT } from "./feedTokens";

/**
 * Share menu for a feed post. Mobile gets the native-style draggable bottom
 * sheet (swipe-to-dismiss); desktop/web keeps the original small anchored
 * dropdown next to the share button — that dropdown pattern reads fine on
 * a pointer-driven surface and a bottom sheet would be an odd fit for it.
 *
 * `actions`: [{ key, label, listLabel?, icon, bg, color, onClick }]
 * `anchorEl`: the share trigger button's DOM node — required for the
 * desktop dropdown to position itself; unused on mobile.
 */
export default function ShareSheet({ open, onClose, title, actions, anchorEl }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!isMobile) {
    return (
      <Popover
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: "-8px",
            minWidth: "220px",
            background: "#fff",
            border: `1px solid ${TOKENS.line}`,
            borderRadius: "12px",
            boxShadow: "0 12px 30px rgba(15,23,42,0.12), 0 4px 8px rgba(15,23,42,0.06)",
            padding: "6px",
          },
        }}
      >
        {title && (
          <Box sx={{ padding: "8px 12px 6px", fontSize: "11px", color: TOKENS.faint, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {title}
          </Box>
        )}
        {actions.map((a) => (
          <Box
            key={a.key}
            component="button"
            type="button"
            onClick={a.onClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: "13px",
              fontWeight: 600,
              color: TOKENS.ink2,
              textAlign: "left",
              "&:hover": { background: TOKENS.surface2 },
            }}
          >
            <Box
              sx={{
                width: "28px",
                height: "28px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                background: a.bg,
                color: a.color || "#fff",
              }}
            >
              {a.icon}
            </Box>
            {a.listLabel || a.label}
          </Box>
        ))}
      </Popover>
    );
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          // The app-wide MuiDrawer theme override (written for the side nav)
          // forces height: calc(100vh - 32px), a translucent blurred
          // background, and a 16px margin on every drawer paper — all of
          // which need overriding here for a content-sized bottom sheet.
          height: "auto",
          maxHeight: "80vh",
          margin: 0,
          backgroundColor: "#fff",
          backdropFilter: "none",
          borderRadius: 0,
          borderTopLeftRadius: "20px",
          borderTopRightRadius: "20px",
          width: "100%",
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: "14px", pb: "6px" }}>
        <Box sx={{ width: "36px", height: "4px", borderRadius: "999px", background: TOKENS.line }} />
      </Box>

      {title && (
        <Box
          sx={{
            fontFamily: FONT,
            fontSize: "15px",
            fontWeight: 700,
            color: TOKENS.ink,
            textAlign: "center",
            padding: "6px 16px 10px",
          }}
        >
          {title}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "20px",
          padding: "12px 16px 32px",
        }}
      >
        {actions.map((a) => (
          <Box
            key={a.key}
            component="button"
            type="button"
            onClick={a.onClick}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              width: "68px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <Box
              sx={{
                width: "58px",
                height: "58px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                background: a.bg,
                color: a.color || "#fff",
              }}
            >
              {a.icon}
            </Box>
            <Box
              sx={{
                fontFamily: FONT,
                fontSize: "11.5px",
                fontWeight: 600,
                color: TOKENS.ink2,
                textAlign: "center",
                lineHeight: 1.2,
              }}
            >
              {a.label}
            </Box>
          </Box>
        ))}
      </Box>
    </SwipeableDrawer>
  );
}
